/**
 * tiles.ts — value formatters for metric tiles that must never be blank.
 *
 * Each helper takes the raw API value (which may be null, undefined, a
 * numeric string, or NaN) and returns display text. A value the payload
 * can't provide renders as an explicit "n/a" — text a screen reader and
 * an automated audit both see — never "" / "undefined" / "NaN".
 *
 * Shared by web (investorDetail / listings / portfolio) and mobile
 * (DealDetailScreen / PortfolioScreen) so both say the same thing.
 */

import { compactMagnitude } from './format.ts';

export const NA = 'n/a';

export function toNumber(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Sale-to-list style ratio (0.985 → "98.5%"). Null / 0 → n/a. */
export function ratioPctTile(v: unknown, digits = 1): string {
  const n = toNumber(v);
  if (n == null || n <= 0) return NA;
  return `${(n * 100).toFixed(digits)}%`;
}

/** Whole-number count tile (days on market, doors). Null → n/a; 0 stays "0"
 *  only when `zeroIsValue` — DOM/median tiles treat 0 as "not measured". */
export function countTile(v: unknown, opts: { zeroIsValue?: boolean } = {}): string {
  const n = toNumber(v);
  if (n == null) return NA;
  if (n === 0 && !opts.zeroIsValue) return NA;
  return String(Math.round(n));
}

/** Annual expense line in the income + expense breakdown. Null → n/a;
 *  a real $0 (e.g. self-managed → no PM fee) reads "$0", not blank. */
export function expenseTile(v: unknown): string {
  const n = toNumber(v);
  if (n == null) return NA;
  const abs = Math.round(Math.abs(n));
  return abs === 0 ? '$0' : `−$${abs.toLocaleString('en-US')}`;
}

/** Compact money tile ($1.1M / $425K). Null / ≤0 → n/a. */
export function moneyCompactTile(v: unknown): string {
  const n = toNumber(v);
  if (n == null || n <= 0) return NA;
  return `$${compactMagnitude(n, { millionDecimals: 1 })}`;
}

/** Amenity / school distance ("0.5 mi"). Null → n/a; 0 → "<0.1 mi". */
export function distanceTile(v: unknown): string {
  const n = toNumber(v);
  if (n == null || n < 0) return NA;
  if (n === 0) return '<0.1 mi';
  return `${n} mi`;
}
